import {
  SearchInput,
  TableCellContentRenderer,
  TableHeaderContentRenderer,
  Tooltip
} from '@/components/basic';
import { ApprovableCell } from '@/components/permissions/ApprovableCell';
import PermissionCell from '@/components/permissions/PermissionCell';
import { PermissionsDatesCell } from '@/components/permissions/PermissionsDatesCell';
import {
  ConfirmOperationModal,
  IResourcePermissionWithResName,
  groupSharedKey
} from '@/components/resources/tabs/access-restrictions/RestrictionsTab';
import {
  AssignmentGroup,
  accessAssignmentDefaultGroup,
  accessAssignmentGroups
} from '@/components/resources/tabs/access-restrictions/defines';
import { classNames } from '@/lib/classNames';
import { AssignmentGroupType, IResourcePermission } from '@gettactic/api';
import { getObjectKeys } from '@gettactic/helpers/src/objects/getObjectKeys';
import { ScrollArea } from '@mantine/core';
import React, { useMemo, useState } from 'react';
import { MdDelete } from 'react-icons/md';
import { Column, Row, useTable } from 'react-table';
import { Checkbox } from 'reakit';

type Props = {
  className?: string;
  data: IResourcePermission[];
  setOperationalModal: React.Dispatch<ConfirmOperationModal | null>;
  inGroups: { [groupKey: string]: IResourcePermissionWithResName[] };
};

interface AssignmentGroupWithRows extends AssignmentGroup {
  rows?: Row<IResourcePermission>[];
}

function bulkKey(perm: IResourcePermission) {
  return `${perm.id}_${perm.resource_id}_${perm.user ? 'user' : 'team'}`;
}

export const SharedPermissionTable = ({
  className,
  data: rawData,
  setOperationalModal,
  inGroups
}: Props): JSX.Element => {
  const [query, setQuery] = useState('');
  const [bulk, setBulk] = useState<string[]>([]);
  const data = useMemo(() => {
    const _query = query.toLowerCase().trim();
    const rawItems = [...rawData];
    const items = _query.length
      ? rawItems.filter((x) =>
          x.user
            ? x.user.name.toLowerCase().includes(_query) ||
              x.user.email.toLowerCase().includes(_query)
            : x.team?.name.toLowerCase().includes(_query)
        )
      : rawItems;

    return items.map((item) => ({
      ...item,
      group: item.access_group_type || accessAssignmentDefaultGroup
    }));
  }, [query, rawData, inGroups]);
  const selectedAll = data.length ? data.length === bulk.length : false;
  const visibleSelected = data.filter((x) => bulk.indexOf(bulkKey(x)) > -1);

  const columns: Column<IResourcePermission>[] = useMemo(
    () => [
      {
        Header() {
          return (
            <TableHeaderContentRenderer>
              <div className="px-2">
                <Checkbox
                  className="rounded text-primary focus:ring-0"
                  checked={selectedAll}
                  onClick={() => {
                    if (!selectedAll) {
                      setBulk((x) => data.map((x) => bulkKey(x)));
                    } else {
                      setBulk([]);
                    }
                  }}
                />
              </div>
            </TableHeaderContentRenderer>
          );
        },
        accessor: undefined,
        id: 'selected',
        Cell({
          row: { original: permission }
        }: { row: Row<IResourcePermission> }) {
          //@ts-expect-error Id is a numeric type so it shouldn't be used with this special string value
          if (permission.id === '*') return null;
          const selected = bulk.indexOf(bulkKey(permission)) > -1;
          return (
            <div className="flex items-center justify-center space-x-2 px-2">
              <Checkbox
                className="rounded text-primary focus:ring-0"
                checked={selected}
                onClick={() => {
                  if (selected) {
                    setBulk((x) => x.filter((s) => s !== bulkKey(permission)));
                  } else {
                    setBulk((x) => [...x, bulkKey(permission)]);
                  }
                }}
              />
            </div>
          );
        }
      },
      {
        Header() {
          return (
            <TableHeaderContentRenderer>
              <div>Assignment</div>
            </TableHeaderContentRenderer>
          );
        },
        accessor: undefined,
        id: 'type',
        maxWidth: 410,
        Cell({
          row: { original: permission },
          column
        }: {
          row: Row<IResourcePermission>;
          column: Column<IResourcePermission>;
        }) {
          return <PermissionCell permission={permission} column={column} />;
        }
      },
      {
        Header() {
          return (
            <TableHeaderContentRenderer>
              <div>Dates</div>
            </TableHeaderContentRenderer>
          );
        },
        accessor: undefined,
        id: 'dates',
        Cell: PermissionsDatesCell
      },
      {
        Header: (
          <div className="flex items-center">
            Approval Required
            <Tooltip>
              <span className="font-normal">
                Users will need a manager to approve a reservation before it's
                accepted by the system.
              </span>
            </Tooltip>
          </div>
        ),
        id: 'edit-approvable-status',
        accessor: undefined,
        Cell: ({
          row: { original: permission }
        }: { row: Row<IResourcePermission> }) => {
          //@ts-expect-error Id is a numeric type so it shouldn't be used with this special string value
          if (permission.id === '*') return null;
          return (
            <ApprovableCell
              permission={permission}
              onChange={(x: IResourcePermission) => {
                setOperationalModal(
                  x
                    ? {
                        operation: 'is_approvable',
                        applyTo: inGroups[groupSharedKey(x)]
                      }
                    : null
                );
              }}
            />
          );
        }
      },
      {
        Header: '',
        id: 'edit-menu-placeholder',
        accessor: undefined,
        Cell: ({ row }: { row: Row<IResourcePermission> }) =>
          TableCellContentRenderer.EditDeleteControls<IResourcePermission>({
            row,
            setDeleteModal: (x) =>
              setOperationalModal(
                x
                  ? {
                      operation: 'delete',
                      //@ts-expect-error Id is a numeric type so it shouldn't be used with this special string value
                      applyTo: x.id === '*' ? [x] : inGroups[groupSharedKey(x)]
                    }
                  : null
              )
          })
      }
    ],
    [bulk, data, selectedAll]
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
    flexRender
  } = useTable<IResourcePermission>({
    columns,
    data
  });

  // TODO improve this
  const sanitizedRows = JSON.stringify(data);

  const groups = React.useMemo(() => {
    const groups: Record<AssignmentGroupType, AssignmentGroupWithRows> =
      getObjectKeys(accessAssignmentGroups).reduce(
        (
          acc: Record<string, AssignmentGroupWithRows>,
          key: AssignmentGroupType
        ) => {
          acc[key] = {
            ...accessAssignmentGroups[key],
            rows: []
          };
          return acc;
        },
        {}
      );
    rows.forEach((row) => {
      const group =
        row.original.access_group_type || accessAssignmentDefaultGroup;
      if (!groups[group]) {
        groups[group] = { rows: [], label: '', color: '', isAdditional: true };
      }
      if (!groups[group].rows) groups[group].rows = [];
      groups[group].rows?.push(row);
    });

    return groups;
  }, [sanitizedRows]);

  return (
    <div className="mt-6 mb-8 flex flex-1 flex-col flex-nowrap items-stretch gap-3 overflow-hidden px-1">
      <div className="flex items-center justify-between gap-4 sm:gap-16">
        <SearchInput query={query} setQuery={setQuery} />
        <div className="flex flex-nowrap">
          {visibleSelected.length ? (
            <p>Selected {visibleSelected.length}</p>
          ) : null}
          <button
            disabled={bulk.length === 0}
            onClick={(ev) => {
              ev.preventDefault();
              const deleteAll: IResourcePermission[] = [];
              visibleSelected.forEach((x) =>
                deleteAll.push(...inGroups[groupSharedKey(x)])
              );
              setOperationalModal({ operation: 'delete', applyTo: deleteAll });
            }}
            className={classNames(
              visibleSelected.length
                ? 'text-gray-600 hover:text-gray-800'
                : 'text-gray-100',
              'px-2'
            )}
          >
            <MdDelete className="h-5 w-5" />
          </button>
        </div>
      </div>
      <ScrollArea
        type="auto"
        className="h-full"
        classNames={{
          root: 'rounded-lg border border border-gray-300'
        }}
      >
        <div className={classNames('flex flex-col', className)}>
          <div className="flex flex-auto flex-col">
            <div className="rounded-md border bg-white shadow-sm">
              <table className="w-full" {...getTableProps()}>
                <thead>
                  {headerGroups.map((headerGroup) => (
                    // eslint-disable-next-line react/jsx-key                        
// biome-ignore lint/correctness/useJsxKeyInIterable: <key's are in spread>
                    // biome-ignore lint/correctness/useJsxKeyInIterable: <explanation>
                    <tr
                      className="border-gray-300 border-b bg-gray-50"
                      {...headerGroup.getHeaderGroupProps()}
                    >
                      {headerGroup.headers.map((column) => (
                        // eslint-disable-next-line react/jsx-key                        
// biome-ignore lint/correctness/useJsxKeyInIterable: <key's are in spread>
                        // biome-ignore lint/correctness/useJsxKeyInIterable: <explanation>
                        <th
                          className="py-3 text-left font-bold text-gray-800"
                          {...column.getHeaderProps()}
                        >
                          {column.render('Header')}
                        </th>
                      ))}
                    </tr>
                  ))}
                </thead>
                {getObjectKeys(groups).map((groupKey) => {
                  const group = groups[groupKey];
                  const rows = groups[groupKey]?.rows || [];

                  if (!rows.length) return null;

                  return (
                    <React.Fragment key={groupKey}>
                      <thead>
                        <tr>
                          <td
                            colSpan={columns.length}
                            className={'border-t border-t-gray-300'}
                          >
                            <div
                              className={
                                'flex items-center gap-6 p-4 font-bold text-sm'
                              }
                            >
                              <div
                                className={
                                  'flex h-10 w-10 items-center justify-center rounded-full bg-primary font-normal text-white'
                                }
                                style={{
                                  backgroundColor: group.color
                                }}
                              >
                                {group.shortLabel || ''}
                              </div>
                              {group.label}
                            </div>
                          </td>
                        </tr>
                      </thead>
                      <tbody {...getTableBodyProps()}>
                        {rows.map((row, index) => {
                          prepareRow(row);
                          return (
                            <tr
                              className="border-gray-300 border-t border-b bg-white last:border-b-0"
                              {...row.getRowProps()}
                              key={index}
                            >
                              {row.cells.map((cell, index) => (
                                <td {...cell.getCellProps()} key={index}>
                                  {cell.render('Cell')}
                                </td>
                              ))}
                            </tr>
                          );
                        })}
                      </tbody>
                    </React.Fragment>
                  );
                })}
              </table>
            </div>
          </div>
        </div>
      </ScrollArea>
    </div>
  );
};
